import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { useAuth } from './useAuth';

interface ImpersonationData {
  userId: string;
  userName: string;
  userEmail: string;
}

interface ImpersonationContextType {
  impersonation: ImpersonationData | null;
  isImpersonating: boolean;
  effectiveUserId: string | null;
  startImpersonation: (data: ImpersonationData) => void;
  stopImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const ImpersonationProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [impersonation, setImpersonation] = useState<ImpersonationData | null>(null);

  useEffect(() => {
    const stored = sessionStorage.getItem('impersonation');
    if (stored) {
      setImpersonation(JSON.parse(stored));
    }
  }, []);

  const startImpersonation = (data: ImpersonationData) => {
    sessionStorage.setItem('impersonation', JSON.stringify(data));
    setImpersonation(data);
  };

  const stopImpersonation = () => {
    sessionStorage.removeItem('impersonation');
    setImpersonation(null);
  };

  const isImpersonating = !!impersonation;
  const effectiveUserId = impersonation?.userId || user?.id || null;

  return (
    <ImpersonationContext.Provider value={{
      impersonation,
      isImpersonating,
      effectiveUserId,
      startImpersonation,
      stopImpersonation,
    }}>
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};